import React, { useEffect, useState } from 'react';
import {
  searchSeasonPasses,
  createSeasonPass,
  getOrderProducts,
} from '../api';
import AppHeader from './AppHeader';
import { US_STATES } from '../utils/usStates';
import { formatPerkLabel, parseEntitlements } from '../utils/entitlements';
import PerksEditor from './PerksEditor';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirth: '',
  phone: '',
  address: '',
  address2: '',
  city: '',
  state: 'MO',
  zip: '',
  seasonPassId: '',
  orderId: '',
  productCode: 'legend_pass',
  entitlements: [],
};

function SeasonPass({ user, onLogout, onNavigate, initialQuery }) {
  const [searchQuery, setSearchQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPass, setNewPass] = useState(emptyForm);
  const [passTypes, setPassTypes] = useState([]);
  const [expandedPassId, setExpandedPassId] = useState(null);


  useEffect(() => {
    getOrderProducts()
      .then((data) => {
        const types = data.seasonPassTypes || [];
        setPassTypes(types);
        if (types[0]) {
          setNewPass((prev) => ({
            ...prev,
            productCode: types[0].code,
            entitlements: parseEntitlements(types[0].entitlements || []),
          }));
        }
      })
      .catch(() => {
        /* optional catalog */
      });
  }, []);

  const runSearch = async (query) => {
    setError('');
    setMessage('');
    setLoading(true);
    setSearched(true);
    try {
      const trimmedQuery = String(query || '').trim();
      if (!trimmedQuery) {
        setResults([]);
        return;
      }
      const data = await searchSeasonPasses(trimmedQuery);
      setResults(data.results || []);
    } catch (err) {
      setError(err.error || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialQuery) {
      setSearchQuery(initialQuery);
      runSearch(initialQuery);
    }
  }, [initialQuery]);

  const handleSearch = async (e) => {
    e.preventDefault();
    await runSearch(searchQuery);
  };

  const refreshSearch = async () => {
    if (!searchQuery.trim()) return;
    await runSearch(searchQuery);
  };

  const applyPassType = (formSetter, productCode) => {
    const type = passTypes.find((entry) => entry.code === productCode);
    formSetter((prev) => ({
      ...prev,
      productCode,
      entitlements: parseEntitlements(type?.entitlements || []),
    }));
  };

  const handleNewChange = (e) => {
    const { name, value } = e.target;
    if (name === 'productCode') {
      applyPassType(setNewPass, value);
      return;
    }
    setNewPass((prev) => ({ ...prev, [name]: value }));
  };

  const passPayload = (form, includeSeasonPassId = false) => {
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      dateOfBirth: form.dateOfBirth,
      phone: form.phone,
      address: form.address,
      address2: form.address2,
      city: form.city,
      state: form.state,
      zip: form.zip,
      orderId: form.orderId,
      productCode: form.productCode,
      entitlements: form.entitlements || [],
    };
    if (includeSeasonPassId) {
      payload.seasonPassId = form.seasonPassId;
    }
    return payload;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const data = await createSeasonPass(passPayload(newPass));
      setShowAddModal(false);
      setNewPass({
        ...emptyForm,
        productCode: passTypes[0]?.code || 'legend_pass',
        entitlements: parseEntitlements(passTypes[0]?.entitlements || []),
      });
      if (data.seasonPass?.id) {
        onNavigate('season-pass-detail', {
          seasonPassId: data.seasonPass.id,
          returnTo: 'season-pass',
        });
        return;
      }
      const createdId = data.seasonPass?.seasonPassId || '';
      setMessage(createdId ? `Season pass ${createdId} created` : 'Season pass created');
      if (searchQuery.trim()) {
        await refreshSearch();
      } else if (createdId) {
        setSearchQuery(createdId);
        await runSearch(createdId);
      }
    } catch (err) {
      setError(err.error || 'Unable to create season pass');
    }
  };

  const formFields = (form, onChange, idPrefix, { includeSeasonPassId = false } = {}) => (
    <>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-productCode`}>Pass Type</label>
        <select
          id={`${idPrefix}-productCode`}
          name="productCode"
          value={form.productCode || ''}
          onChange={onChange}
        >
          {passTypes.map((type) => (
            <option key={type.code} value={type.code}>
              {type.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-firstName`}>First Name *</label>
        <input
          id={`${idPrefix}-firstName`}
          name="firstName"
          value={form.firstName}
          onChange={onChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-lastName`}>Last Name *</label>
        <input
          id={`${idPrefix}-lastName`}
          name="lastName"
          value={form.lastName}
          onChange={onChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-email`}>Email</label>
        <input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-dateOfBirth`}>Date of Birth</label>
        <input
          id={`${idPrefix}-dateOfBirth`}
          name="dateOfBirth"
          type="date"
          value={form.dateOfBirth}
          onChange={onChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-phone`}>Phone Number</label>
        <input
          id={`${idPrefix}-phone`}
          name="phone"
          value={form.phone}
          onChange={onChange}
          placeholder="314-555-0100"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-address`}>Address</label>
        <input
          id={`${idPrefix}-address`}
          name="address"
          value={form.address}
          onChange={onChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-address2`}>Address 2</label>
        <input
          id={`${idPrefix}-address2`}
          name="address2"
          value={form.address2 || ''}
          onChange={onChange}
        />
      </div>
      <div className="order-address-grid">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-city`}>City</label>
          <input
            id={`${idPrefix}-city`}
            name="city"
            value={form.city || ''}
            onChange={onChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-state`}>State</label>
          <select
            id={`${idPrefix}-state`}
            name="state"
            value={form.state || ''}
            onChange={onChange}
          >
            <option value="">Select</option>
            {US_STATES.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-zip`}>Zip Code</label>
          <input
            id={`${idPrefix}-zip`}
            name="zip"
            value={form.zip || ''}
            onChange={onChange}
          />
        </div>
      </div>
      {includeSeasonPassId && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-seasonPassId`}>Season Pass ID *</label>
          <input
            id={`${idPrefix}-seasonPassId`}
            name="seasonPassId"
            value={form.seasonPassId}
            onChange={onChange}
            required
          />
        </div>
      )}
      {includeSeasonPassId && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-orderId`}>Order ID</label>
          <input
            id={`${idPrefix}-orderId`}
            name="orderId"
            value={form.orderId || ''}
            onChange={onChange}
            placeholder="ORD-..."
          />
        </div>
      )}
    </>
  );

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="Season Pass"
        subtitle="Search season pass holders, then open a full guest profile."
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="season-pass"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="section-header">
        <p className="subtitle" style={{ marginBottom: 0 }}>
          Search by season pass ID, order ID, email, phone, city, zip, or name.
        </p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setShowAddModal(true);
            setError('');
          }}
        >
          + Add Season Pass
        </button>
      </div>

      <form onSubmit={handleSearch} style={{ marginTop: '16px' }}>
        <div className="search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Season pass ID, order ID, email, phone, or name..."
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {searched && (
        <div className="search-results">
          <h2>
            Results for &quot;{searchQuery}&quot; ({results.length} found)
          </h2>
          {results.length > 0 ? (
            <div className="table-scroll">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Season Pass ID</th>
                    <th>Pass Type</th>
                    <th>Order</th>
                    <th>Name</th>
                    <th>City</th>
                    <th>Perks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((pass) => (
                    <React.Fragment key={pass.id}>
                      <tr>
                        <td>{pass.seasonPassId}</td>
                        <td>{pass.productName || pass.productCode || '—'}</td>
                        <td>
                          {pass.orderId ? (
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => onNavigate('orders', { orderId: pass.orderId })}
                            >
                              {pass.orderId}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {pass.firstName} {pass.lastName}
                        </td>
                        <td>
                          {[pass.city, pass.state, pass.zip].filter(Boolean).join(', ')}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() =>
                              setExpandedPassId((prev) => (prev === pass.id ? null : pass.id))
                            }
                          >
                            {(pass.entitlements || []).length} entitlements
                          </button>
                        </td>
                        <td className="table-actions-cell">
                          <button
                            type="button"
                            className="btn-table"
                            onClick={() =>
                              onNavigate('season-pass-detail', {
                                seasonPassId: pass.id,
                                returnTo: 'season-pass',
                              })
                            }
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                      {expandedPassId === pass.id && (
                        <tr className="entitlements-row">
                          <td colSpan={7}>
                            <strong>Entitlements</strong>
                            {(pass.entitlements || []).length ? (
                              <ul className="entitlements-list">
                                {(pass.entitlements || []).map((item, index) => (
                                  <li key={`${formatPerkLabel(item)}-${index}`}>
                                    <strong>{formatPerkLabel(item)}</strong>
                                    {item?.description ? (
                                      <span className="subtitle"> — {item.description}</span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="subtitle">No entitlements on this pass.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">No season passes found.</div>
          )}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add Season Pass</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <p className="subtitle" style={{ marginBottom: '16px' }}>
                Season pass ID is generated automatically. Pass type sets the default
                entitlements.
              </p>
              {formFields(newPass, handleNewChange, 'new')}
              <PerksEditor
                idPrefix="new-pass-perk"
                perks={newPass.entitlements || []}
                onChange={(entitlements) =>
                  setNewPass((prev) => ({ ...prev, entitlements }))
                }
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit">Create Season Pass</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SeasonPass;
